import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Search, X, Check, ArrowUpDown } from 'lucide-react';

export interface FilterChipOption {
    id: string;
    label: string;
    icon?: React.ReactNode;
}

interface FilterPanelProps {
    open: boolean;
    title: string;
    icon?: React.ReactNode;
    options: FilterChipOption[];
    selected: string[];
    singleSelect?: boolean;
    searchable?: boolean;
    onToggle: (id: string) => void;
    onClose: () => void;
    onClear: () => void;
    onDone?: () => void;
}

export const FilterPanel: React.FC<FilterPanelProps> = ({
    open,
    title,
    icon,
    options,
    selected,
    singleSelect = false,
    searchable = false,
    onToggle,
    onClose,
    onClear,
    onDone,
}) => {
    const [q, setQ] = useState('');

    if (!open) return null;

    const list = q.trim()
        ? options.filter(o => o.label.toLowerCase().includes(q.toLowerCase()) || o.id.toLowerCase().includes(q.toLowerCase()))
        : options;

    const handleToggle = (id: string) => {
        if (singleSelect) {
            onToggle(selected.includes(id) ? '' : id);
        } else {
            onToggle(id);
        }
    };

    return createPortal(
        <>
            <style>{`
                .fp-fade { animation: fpFade .18s ease-out }
                .fp-sheet { animation: fpUp .32s cubic-bezier(.32,.72,.24,.9) }
                @keyframes fpFade { from { opacity: 0 } to { opacity: 1 } }
                @keyframes fpUp { from { transform: translateY(80px); opacity: .5 } to { transform: translateY(0); opacity: 1 } }
            `}</style>
            <div className="fixed inset-0 z-[200] flex items-end justify-center">
                <div className="absolute inset-0 bg-black/70 backdrop-blur-sm fp-fade" onClick={onClose} />

                <div className="relative w-full max-w-md glass-strong rounded-t-[32px] border-t border-white/10 p-4 pb-8 flex flex-col max-h-[80vh] shadow-2xl z-10 fp-sheet overscroll-contain">
                    <div className="w-10 h-1 bg-white/20 rounded-full mx-auto mb-4" />

                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-black text-white flex items-center gap-2.5" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                            {icon}
                            <span>{title}</span>
                            {selected.length > 0 && (
                                <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/10 text-white/70 font-mono">
                                    {selected.length} selected
                                </span>
                            )}
                        </h3>
                        <button
                            onClick={onClose}
                            className="p-2 bg-white/10 hover:bg-white/20 rounded-full text-white/50 hover:text-white transition-colors"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>

                    {searchable && (
                        <div className="flex items-center gap-2 mb-3">
                            <div className="flex-1 glass-inset rounded-full pl-[38px] pr-3 h-10 relative flex items-center">
                                <Search className="w-4 h-4 text-white/40 absolute left-3" />
                                <input
                                    type="text"
                                    placeholder="Search..."
                                    value={q}
                                    onChange={(e) => setQ(e.target.value)}
                                    className="bg-transparent text-sm text-white placeholder-white/35 font-semibold outline-none w-full"
                                />
                            </div>
                            <button
                                onClick={onClear}
                                className="px-3.5 h-10 rounded-full glass-inset border border-white/10 text-white/60 text-xs font-bold hover:text-white transition-colors"
                            >
                                Clear
                            </button>
                        </div>
                    )}

                    <div className="overflow-y-auto no-scrollbar flex-1">
                        {list.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-10 text-white/40">
                                <ArrowUpDown className="w-7 h-7 mb-2 opacity-50" />
                                <span className="text-sm">No options</span>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 gap-2">
                                {list.map((op) => {
                                    const isOn = selected.includes(op.id);
                                    return (
                                        <button
                                            key={op.id}
                                            onClick={() => handleToggle(op.id)}
                                            className={`flex items-center justify-between gap-3 px-3.5 py-3 rounded-2xl text-sm font-bold transition-all active:scale-[0.98] border ${
                                                isOn
                                                    ? 'bg-gradient-to-r from-purple-500/25 to-pink-500/25 border-purple-400/40 text-white'
                                                    : 'glass-inset border-white/10 text-white/70 hover:text-white'
                                            }`}
                                        >
                                            <span className="flex items-center gap-2.5 min-w-0">
                                                {op.icon}
                                                <span className="truncate">{op.label}</span>
                                            </span>
                                            <span
                                                className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 transition-all ${
                                                    isOn
                                                        ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white'
                                                        : 'border-2 border-white/25'
                                                }`}
                                            >
                                                {isOn && <Check className="w-3.5 h-3.5 stroke-[3]" />}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    <div className="pt-3 flex items-center gap-3 border-t border-white/10 mt-3">
                        <button
                            onClick={onClear}
                            className="flex-1 py-3 glass-inset text-white/80 rounded-2xl font-bold text-sm transition-all active:scale-95"
                        >
                            Clear all
                        </button>
                        <button
                            onClick={() => { if (onDone) onDone(); onClose(); }}
                            className="flex-1 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-2xl font-bold text-sm shadow-lg shadow-purple-500/25 transition-all active:scale-95"
                        >
                            {onDone ? 'Done' : 'Close'}
                        </button>
                    </div>
                </div>
            </div>
        </>,
        document.body
    );
};

interface PricePanelProps {
    open: boolean;
    title: string;
    min: number | null;
    max: number | null;
    onMinChange: (v: number | null) => void;
    onMaxChange: (v: number | null) => void;
    onClose: () => void;
    onClear: () => void;
    onApply: () => void;
}

export const PricePanel: React.FC<PricePanelProps> = ({
    open, title, min, max, onMinChange, onMaxChange, onClose, onClear, onApply,
}) => {
    if (!open) return null;

    return createPortal(
        <>
            <style>{`
                .fp-fade { animation: fpFade .18s ease-out }
                .fp-sheet { animation: fpUp .32s cubic-bezier(.32,.72,.24,.9) }
                @keyframes fpFade { from { opacity: 0 } to { opacity: 1 } }
                @keyframes fpUp { from { transform: translateY(80px); opacity: .5 } to { transform: translateY(0); opacity: 1 } }
            `}</style>
            <div className="fixed inset-0 z-[200] flex items-end justify-center">
                <div className="absolute inset-0 bg-black/70 backdrop-blur-sm fp-fade" onClick={onClose} />

                <div className="relative w-full max-w-md glass-strong rounded-t-[32px] border-t border-white/10 p-4 pb-8 flex flex-col shadow-2xl z-10 fp-sheet">
                    <div className="w-10 h-1 bg-white/20 rounded-full mx-auto mb-4" />

                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-black text-white flex items-center gap-2.5" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                            <ArrowUpDown className="w-4 h-4 text-purple-300" />
                            <span>{title}</span>
                        </h3>
                        <button onClick={onClose} className="p-2 bg-white/10 hover:bg-white/20 rounded-full text-white/50 hover:text-white transition-colors">
                            <X className="w-4 h-4" />
                        </button>
                    </div>

                    <div className="flex items-center gap-3 mb-6">
                        <div className="flex-1 glass-inset rounded-2xl p-3.5">
                            <label className="text-[10px] text-white/40 uppercase tracking-wider font-semibold block mb-1">Min (GRAM)</label>
                            <input
                                type="number"
                                inputMode="decimal"
                                placeholder="No min"
                                value={min ?? ''}
                                onChange={(e) => onMinChange(e.target.value === '' ? null : Number(e.target.value))}
                                className="w-full bg-transparent text-white text-lg font-bold focus:outline-none"
                            />
                        </div>
                        <div className="flex-1 glass-inset rounded-2xl p-3.5">
                            <label className="text-[10px] text-white/40 uppercase tracking-wider font-semibold block mb-1">Max (GRAM)</label>
                            <input
                                type="number"
                                inputMode="decimal"
                                placeholder="No max"
                                value={max ?? ''}
                                onChange={(e) => onMaxChange(e.target.value === '' ? null : Number(e.target.value))}
                                className="w-full bg-transparent text-white text-lg font-bold focus:outline-none"
                            />
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        <button
                            onClick={onClear}
                            className="flex-1 py-3.5 glass-inset text-white/80 rounded-2xl font-bold text-sm transition-all active:scale-95"
                        >
                            Clear
                        </button>
                        <button
                            onClick={() => { onApply(); onClose(); }}
                            className="flex-[2] py-3.5 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-2xl font-bold text-sm shadow-lg shadow-purple-500/25 transition-all active:scale-95"
                        >
                            Apply
                        </button>
                    </div>
                </div>
            </div>
        </>,
        document.body
    );
};